import { FormEvent, useMemo, useState } from "react";
import { ArrowRight, Mail, ShieldCheck } from "lucide-react";

import { Button, Card, StatusPill } from "./ui";

import { apiPost } from "../lib/api";

type FormState = "idle" | "submitting" | "success" | "error";

export function ContactPage() {
  const submittedAt = useMemo(() => Date.now(), []);
  const [state, setState] = useState<FormState>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    setError("");

    const form = new FormData(event.currentTarget);
    const payload = {
      name: String(form.get("name") || ""),
      email: String(form.get("email") || ""),
      topic: String(form.get("topic") || ""),
      message: String(form.get("message") || ""),
      website: String(form.get("website") || ""),
      submittedAt,
    };

    try {
      await apiPost<{ sent: true }>("/contact", payload);

      event.currentTarget.reset();
      setState("success");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Message failed to send.");
      setState("error");
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-10 px-6 py-24 lg:grid-cols-[0.82fr_1fr]">
        <div>
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Contact</span>
          </div>
          <h1 className="mb-7 font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">Talk to iBox</h1>
          <p className="max-w-xl text-lg leading-relaxed text-muted md:text-xl">
            Questions, partnership ideas, feature requests, and support notes can all start here.
          </p>

          <div className="mt-8 flex flex-col gap-3 text-sm text-muted">
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-primary" />
              <span>Messages go straight to the iBox team.</span>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck size={18} className="text-primary" />
              <span>Protected by quiet anti-spam checks.</span>
            </div>
          </div>
        </div>

        <Card interactive={false} className="p-0">
          <div className="flex items-start justify-between gap-4 border-b border-border p-6">
            <div>
              <StatusPill status="info" label="DIRECT MESSAGE" />
              <h2 className="mt-5 font-display text-3xl font-bold uppercase leading-none text-text">Contact Form</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted">Send a note and we will follow up as soon as we can.</p>
            </div>
          </div>

          <form className="space-y-5 p-6" onSubmit={handleSubmit}>
            <input className="hidden" tabIndex={-1} autoComplete="off" name="website" aria-hidden="true" />

            <div className="grid gap-5 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Name</span>
                <input
                  required
                  name="name"
                  className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                  placeholder="Your name"
                />
              </label>
              <label className="space-y-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Email</span>
                <input
                  required
                  type="email"
                  name="email"
                  className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                  placeholder="you@example.com"
                />
              </label>
            </div>

            <label className="block space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Topic</span>
              <input
                required
                name="topic"
                className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                placeholder="Partnership, support, feature request..."
              />
            </label>

            <label className="block space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Message</span>
              <textarea
                required
                name="message"
                rows={7}
                className="w-full resize-none rounded-md border border-border bg-surface-2 px-4 py-3 text-sm leading-relaxed text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                placeholder="Tell us what you need..."
              />
            </label>

            {state === "success" ? <p className="text-sm font-semibold text-success">Message sent. We will get back to you soon.</p> : null}
            {state === "error" ? <p className="text-sm font-semibold text-error">{error}</p> : null}

            <Button className="w-full" type="submit" disabled={state === "submitting"}>
              {state === "submitting" ? "Sending" : "Send Message"} <ArrowRight size={14} />
            </Button>
          </form>
        </Card>
      </section>
    </main>
  );
}
